/**
 * Platform-wide constant enums, allowlists, limits, ports, and the model registry.
 *
 * These are plain data (no zod, no side effects) so any package can import them
 * cheaply. Zod schemas that wrap these live in api-types.ts.
 */
/* -------------------------------------------------------------------------- */
/* Roles and account status                                                   */
/* -------------------------------------------------------------------------- */
export const ROLES = ['mentor', 'parent', 'student'];
/**
 * Account status (users.status).
 *  - `merged` marks an account that was folded into another during an account
 *    merge. Everything it owned moved to the kept account; a merged account can
 *    no longer sign in. (accounts module migration range; pairs with
 *    users.merged_into.)
 */
export const USER_STATUSES = ['pending', 'approved', 'paused', 'merged'];
/* -------------------------------------------------------------------------- */
/* Identity: usernames and hats (SPEC.md "Identity" section)                  */
/* -------------------------------------------------------------------------- */
/** Username length bounds: 3 to 20 characters. */
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;
/** Allowed username characters: letters, numbers, and dashes. */
export const USERNAME_CHARSET = /^[A-Za-z0-9-]+$/;
/**
 * Names no account may take. Kept lowercase; the check lowercases the candidate
 * first so "Admin" and "ADMIN" are caught too. These collide with platform
 * surfaces, routes, or subdomains, or are confusing sentinel words.
 */
export const RESERVED_USERNAMES = [
    'admin',
    'api',
    'app',
    'www',
    'mentor',
    'parent',
    'root',
    'support',
    'proteam',
    'sessions',
    'help',
    'about',
    'settings',
    'null',
    'undefined',
];
/**
 * The account's "hats": the capability surfaces one login can hold at once
 * (SPEC.md "One person, multiple hats"). The top-nav hat switcher flips between
 * these; only hats the account actually has appear.
 *  - `projects`  building surface (My Projects). Every account that can build.
 *  - `students`  mentor surface (My Students). Mentor-role accounts.
 *  - `kids`      parent surface (My Kids). Accounts with linked kids.
 */
export const HATS = ['projects', 'students', 'kids'];
/**
 * What an invite code establishes when a kid claims it. Parent-kid linking for
 * email-less kids (SPEC.md identity section): a parent mints a code, the kid
 * enters it, and the kid's account links to that parent.
 */
export const INVITE_CODE_KINDS = ['parent_link'];
/* -------------------------------------------------------------------------- */
/* Project types and creation modes                                           */
/* -------------------------------------------------------------------------- */
/** The concrete template kind stored on projects.type. */
export const PROJECT_TYPES = ['webapp', 'website', 'game2d', 'game3d', 'iphone'];
/** The three top-level choices a student picks first at creation. */
export const CREATION_MODES = ['game', 'app', 'web'];
/** Which project types belong to each creation mode. */
export const PROJECT_TYPES_BY_MODE = {
    game: ['game2d', 'game3d'],
    app: ['webapp', 'iphone'],
    web: ['website'],
};
/* -------------------------------------------------------------------------- */
/* Session modes and status                                                   */
/* -------------------------------------------------------------------------- */
export const SESSION_MODES = ['build', 'learn'];
export const SESSION_STATUSES = ['starting', 'running', 'idle', 'stopped', 'error'];
/**
 * sessions.run_state — how the session's agent loop is currently running.
 *  - `interactive`  normal back-and-forth: the agent works one request at a time.
 *  - `autonomous`   a Big Build long run: the agent grinds the task list on its own.
 *  - `paused_limit` an autonomous run stopped at a usage cap; it resumes when limits reset.
 * Stored NOT NULL DEFAULT 'interactive' (sessions module migration range, like chat_only).
 */
export const SESSION_RUN_STATES = ['interactive', 'autonomous', 'paused_limit'];
/* -------------------------------------------------------------------------- */
/* Agent naming (ARCHITECTURE.md section 11)                                   */
/* -------------------------------------------------------------------------- */
/**
 * Curated, kid-friendly Greek mythology names for worker subagents. The runner
 * assigns the next unused name in this pool to each new worker and tells the kid
 * in plain words who is doing what ("Athena is drawing your sprites"). Kept light
 * and friendly on purpose: nothing dark, scary, or gloomy.
 */
export const AGENT_NAME_POOL = [
    'Athena',
    'Apollo',
    'Hermes',
    'Artemis',
    'Iris',
    'Atlas',
    'Daedalus',
    'Helios',
    'Selene',
    'Orion',
    'Nike',
    'Echo',
    'Calliope',
    'Theseus',
    'Perseus',
    'Hestia',
    'Pegasus',
    'Triton',
    'Gaia',
    'Demeter',
    'Clio',
    'Leto',
    'Rhea',
    'Eos',
];
/**
 * Fun, friendly starter ideas for the account's own AI name, offered at
 * onboarding (the kid can also type their own). Every choice still runs through
 * the content flagger. Plain and playful, no jargon, no AI-sounding words.
 */
export const AI_NAME_SUGGESTIONS = [
    'Sparky',
    'Pixel',
    'Bolt',
    'Nova',
    'Ziggy',
    'Comet',
    'Milo',
    'Waffles',
    'Biscuit',
    'Coco',
    'Pip',
    'Sunny',
];
/* -------------------------------------------------------------------------- */
/* Publishing and review                                                      */
/* -------------------------------------------------------------------------- */
export const PUBLISH_STATES = ['draft', 'pending_approval', 'published'];
export const REVIEW_MODES = ['direct', 'pull_request'];
export const REVIEW_REQUEST_STATUSES = ['open', 'merged', 'closed'];
export const CUSTOM_DOMAIN_STATUSES = ['pending', 'active'];
/* -------------------------------------------------------------------------- */
/* Funding sources, virtual keys, credential scope                            */
/* -------------------------------------------------------------------------- */
export const FUNDING_KINDS = ['api_key', 'oauth'];
export const FUNDING_PROVIDERS = ['anthropic', 'openai', 'kimi', 'minimax', 'xai'];
/**
 * Providers that can be funded with a raw API key. Kimi and MiniMax are funded
 * with a coding-subscription API key (they speak the Anthropic Messages API with
 * a Bearer key), so they are api_key providers as well as OAuth providers.
 */
export const API_KEY_PROVIDERS = ['anthropic', 'openai', 'kimi', 'minimax'];
/** The only providers that can be funded by a subscription OAuth connection. */
export const OAUTH_PROVIDERS = ['openai', 'kimi', 'minimax'];
/** OpenAI GPT-5.6 subscription tiers (model_tier on an openai oauth funding source). */
export const OPENAI_OAUTH_TIERS = ['sol', 'terra', 'luna'];
export const FUNDING_SOURCE_STATUSES = ['active', 'needs_reauth', 'disabled'];
export const VIRTUAL_KEY_STATUSES = ['active', 'paused', 'revoked'];
/** Credential reuse scope. */
export const CREDENTIAL_SCOPES = ['account', 'project'];
/* -------------------------------------------------------------------------- */
/* Assets, repos, messages, flags, moderation                                 */
/* -------------------------------------------------------------------------- */
export const ASSET_PURPOSES = ['asset', 'reference'];
export const MODERATION_STATUSES = ['pending', 'clean', 'flagged', 'blocked'];
export const REPO_KINDS = ['platform', 'github_mirror'];
export const REPO_STATUSES = ['pending', 'active', 'error'];
/** agent_messages.sender. "kid" is the student, "agent" is the model, "system" is the platform. */
export const MESSAGE_SENDERS = ['kid', 'agent', 'system'];
export const FLAG_STATUSES = ['open', 'reviewed'];
/* -------------------------------------------------------------------------- */
/* Builds, billing, consents, notifications                                   */
/* -------------------------------------------------------------------------- */
export const BUILD_PLATFORMS = ['ios'];
export const BUILD_STATUSES = [
    'pending',
    'in_queue',
    'in_progress',
    'finished',
    'errored',
    'canceled',
];
export const BILLING_SUBSCRIPTION_STATUSES = [
    'active',
    'trialing',
    'past_due',
    'canceled',
    'incomplete',
    'incomplete_expired',
    'unpaid',
    'paused',
];
/** COPPA consent methods a parent can use to verify. */
export const CONSENT_METHODS = ['signed_form', 'credit_card', 'video_call', 'email'];
export const NOTIFICATION_KINDS = [
    'flag',
    'approval',
    'publish',
    'usage_limit',
    'build',
    'message',
    'badge',
    'consent',
    'needs_reauth',
    'system',
];
/* -------------------------------------------------------------------------- */
/* Upload allowlist                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Lowercase file extension (no dot) mapped to its canonical MIME type.
 * heic uploads are auto-converted to jpg on the server.
 */
export const UPLOAD_EXTENSION_MIME = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ttf: 'font/ttf',
    otf: 'font/otf',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
};
/** MIME types accepted by the file-bank upload endpoint. */
export const UPLOAD_MIME_ALLOWLIST = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/heic',
    'audio/mpeg',
    'audio/wav',
    'font/ttf',
    'font/otf',
    'text/plain',
    'text/csv',
    'application/json',
];
/** Image MIME types (subset of the allowlist) usable as agent vision input and moderated on upload. */
export const IMAGE_MIMES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/heic',
];
/** heic is converted to this MIME on upload. */
export const HEIC_MIME = 'image/heic';
export const HEIC_CONVERTED_MIME = 'image/jpeg';
export function isUploadMime(mime) {
    return UPLOAD_MIME_ALLOWLIST.includes(mime);
}
export function isImageMime(mime) {
    return IMAGE_MIMES.includes(mime);
}
/* -------------------------------------------------------------------------- */
/* Size limits                                                                */
/* -------------------------------------------------------------------------- */
/** 10 MB per uploaded file. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** 100 MB total per project file bank. */
export const MAX_PROJECT_BYTES = 100 * 1024 * 1024;
/** Data API record value cap (64 KB of JSON). */
export const MAX_DATA_RECORD_BYTES = 64 * 1024;
/* -------------------------------------------------------------------------- */
/* Ports and dev topology (mirrors ARCHITECTURE.md section 4)                 */
/* -------------------------------------------------------------------------- */
export const PORTS = {
    /** API HTTP + WebSocket upgrade. */
    api: 4300,
    /** Web (Vite) dev server. */
    webDev: 4301,
    /** Postgres host-published port (container listens on 5432). */
    postgresHost: 5433,
    postgresContainer: 5432,
    /** Gitea HTTP and SSH host ports. */
    giteaHttp: 4303,
    giteaSsh: 4304,
    /** Caddy. */
    caddyHttp: 80,
    caddyHttps: 443,
    /** Vite dev server inside a session container. */
    sessionVite: 5173,
    /** Egress allowlist forward proxy. */
    egressProxy: 3128,
};
/**
 * The exact model set from ARCHITECTURE.md section 8. Pricing numbers are
 * placeholder defaults meant to be overridden by env at the gateway.
 */
export const PROVIDER_MODELS = {
    anthropic: [
        { id: 'claude-opus-4-8', provider: 'anthropic', displayName: 'Claude Opus 4.8', inputPer1M: 15, outputPer1M: 75 },
        { id: 'claude-sonnet-5', provider: 'anthropic', displayName: 'Claude Sonnet 5', inputPer1M: 3, outputPer1M: 15 },
        { id: 'claude-haiku-4-5', provider: 'anthropic', displayName: 'Claude Haiku 4.5', inputPer1M: 0.8, outputPer1M: 4 },
    ],
    // GPT-5.6 sol/terra/luna are served by a flat ChatGPT subscription (Codex
    // OAuth), not metered per token, so their per-token dollar cost is 0. Dollar
    // caps (dollars_per_month) therefore do not meaningfully apply to these
    // models; the tokens/day and rpm caps DO still apply and are enforced from the
    // token counts the backend reports. If OpenAI ever bills these per token,
    // override with GATEWAY_PRICE_GPT_5_6_{SOL,TERRA,LUNA}_{IN,OUT}.
    openai: [
        { id: 'gpt-5.6-sol', provider: 'openai', displayName: 'GPT-5.6 Sol', inputPer1M: 0, outputPer1M: 0 },
        { id: 'gpt-5.6-terra', provider: 'openai', displayName: 'GPT-5.6 Terra', inputPer1M: 0, outputPer1M: 0 },
        { id: 'gpt-5.6-luna', provider: 'openai', displayName: 'GPT-5.6 Luna', inputPer1M: 0, outputPer1M: 0 },
    ],
    // PLACEHOLDER PRICING — confirm Kimi/MiniMax coding-plan rates before billing on
    // them. These are reasonable per-1M-token estimates so cost math and caps work
    // today; override per-model at the gateway with GATEWAY_PRICE_<MODEL>_{IN,OUT}.
    kimi: [
        { id: 'kimi-k3', provider: 'kimi', displayName: 'Kimi K3', inputPer1M: 0.6, outputPer1M: 2.5 },
    ],
    minimax: [
        { id: 'minimax-m3', provider: 'minimax', displayName: 'MiniMax M3', inputPer1M: 0.3, outputPer1M: 1.2 },
    ],
    // Grok 4.7 is platform-provided through the local xAI proxy. Families do not
    // connect a key for it. Never add grok-4.6.
    xai: [
        { id: 'grok-4.7', provider: 'xai', displayName: 'Grok 4.7', inputPer1M: 0, outputPer1M: 0 },
    ],
};
/** Flat list of every known model. */
export const ALL_MODELS = Object.values(PROVIDER_MODELS).flat();
export const MODEL_IDS = ALL_MODELS.map((m) => m.id);
/** Which single model an OpenAI-OAuth tier unlocks. */
export const OPENAI_TIER_MODEL = {
    sol: 'gpt-5.6-sol',
    terra: 'gpt-5.6-terra',
    luna: 'gpt-5.6-luna',
};
/** Default model the agent uses when a session does not override it. */
export const DEFAULT_AGENT_MODEL = 'claude-sonnet-5';
export function getModel(id) {
    return ALL_MODELS.find((m) => m.id === id);
}
export function modelsForProvider(provider) {
    return PROVIDER_MODELS[provider];
}
export function isKnownModel(id) {
    return ALL_MODELS.some((m) => m.id === id);
}
/** The funding provider that serves a model id, or undefined for an unknown id. */
export function providerForModel(id) {
    return getModel(id)?.provider;
}
/**
 * Kid-friendly labels for the build model picker. Plain and inviting, no jargon,
 * no version numbers, no em dashes. Any model without an entry falls back to its
 * registry displayName via `modelPickerLabel`.
 */
/** The only helpers a kid may pick. Wire ids stay gpt-5.6. Do not invent gpt-6. */
export const KID_BUILD_MODELS = ['gpt-5.6-luna', 'gpt-5.6-sol', 'gpt-5.6-terra', 'grok-4.7'];
/** Platform Grok. Always offered. Never grok-4.6. */
export const PLATFORM_GROK_MODEL = 'grok-4.7';
export const MODEL_KID_LABELS = {
    'kimi-k3': 'Speedy Helper',
    'minimax-m3': 'Mini Helper',
    'gpt-5.6-sol': 'Sol',
    'gpt-5.6-terra': 'Terra',
    'gpt-5.6-luna': 'Luna',
    'grok-4.7': 'Grok 4.7',
};
/** The label to show a kid in the model picker for a model id. */
export function modelPickerLabel(id) {
    return MODEL_KID_LABELS[id] ?? getModel(id)?.displayName ?? id;
}
/**
 * Compute the USD cost of a call from token counts using a model's default
 * pricing. Returns 0 for unknown models (the gateway may override pricing).
 */
export function computeCostUsd(model, tokensIn, tokensOut) {
    const info = getModel(model);
    if (!info)
        return 0;
    return (tokensIn / 1_000_000) * info.inputPer1M + (tokensOut / 1_000_000) * info.outputPer1M;
}
//# sourceMappingURL=constants.js.map